// Casting.jsx
import { useMemo, useRef, useState } from "react";
import "./Casting.css";

const NAV_OFFSET = 86;

// Works for Vite (VITE_*) and CRA (REACT_APP_*)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_CONTACT_API_URL) ||
  process.env.REACT_APP_CONTACT_API_URL ||
  "https://xjref277ki.execute-api.us-west-2.amazonaws.com";

// If your API expects a path like "/contact" or "/prod/contact", change it here:
const CONTACT_ENDPOINT = API_BASE;

const RoleCard = ({ title, tag, desc, selected, onSelect }) => (
  <div className={`cast-card ${selected ? "is-selected" : ""}`}>
    <div className="cast-cardTop">
      <div className="cast-cardTitleWrap">
        <h3 className="cast-cardTitle">{title}</h3>
        <span className="cast-tag">{tag}</span>
      </div>
    </div>

    <p className="cast-cardDesc">{desc}</p>

    <button
      type="button"
      className={`psoc-btn ${selected ? "psoc-btnPrimary" : "psoc-btnSecondary"} cast-cardBtn`}
      onClick={onSelect}
    >
      {selected ? "Selected" : "Apply for this role"}
    </button>
  </div>
);

export default function Casting() {
  const applyRef = useRef(null);

  const [selectedRole, setSelectedRole] = useState("Extra");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | sending | success | error

  const roles = useMemo(
    () => [
      {
        title: "Extra",
        tag: "No experience needed",
        desc: "Fill out a brief form. Flexible shoot times. Great for anyone who wants to be part of a local film set.",
      },
      {
        title: "Lead Supporting Character 1",
        tag: "Memorization required",
        desc: "A key character in the story. Must be comfortable memorizing lines and taking direction on set.",
      },
      {
        title: "Lead Supporting Character 2",
        tag: "Experience preferred",
        desc: "Another major character. Stage presence and acting experience is a plus, but strong auditions matter most.",
      },
      {
        title: "Videographer",
        tag: "Crew",
        desc: "Help us film. Camera experience required. Personal gear not required. Just skill and reliability.",
      },
      {
        title: "Audio Technician",
        tag: "Crew",
        desc: "Responsible for clean audio capture. Boom + lav experience is helpful, but strong fundamentals work too.",
      },
    ],
    []
  );

  const scrollToApply = (role) => {
    if (role) setSelectedRole(role);

    if (!applyRef.current) return;
    const y = applyRef.current.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const safeJson = async (res) => {
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { message: text };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const form = e.currentTarget;

    const formData = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      phone: form.phone.value.trim(),
      subject: `Casting: ${selectedRole}`,
      message: form.message.value.trim(),
      role: selectedRole,
      sourcePage: "Casting",
    };

    setSubmitting(true);
    setStatus({ state: "sending", message: "Sending…" });

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg = data?.message || "There was an issue submitting the form.";
        throw new Error(msg);
      }

      setStatus({ state: "success", message: data?.message || "Submitted! We’ll reach out soon." });
      form.reset();
      setSelectedRole("Extra");
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        message: err?.message || "There was an error submitting the form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psoc cast">
      {/* Hero */}
      <section className="cast-hero">
        <div className="psoc-container cast-heroGrid">
          <div className="cast-heroText">
            <p className="psoc-badge">Casting • Local shoot • Short film</p>
            <h1 className="psoc-h1">Casting</h1>
            <p className="psoc-lead">
              Production Studio of Colorado LLC is seeking talented actors and crew for an upcoming short film to be shot locally.
              If you’re reliable, easy to work with, and excited to create. Apply below.
            </p>

            <div className="cast-ctaRow">
              <button className="psoc-btn psoc-btnPrimary" type="button" onClick={() => scrollToApply(selectedRole)}>
                Apply Now
              </button>
              <button className="psoc-btn psoc-btnSecondary" type="button" onClick={() => window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" })}>
                View Roles
              </button>
            </div>

            <div className="cast-pillRow">
              <span className="cast-pill">Clear expectations</span>
              <span className="cast-pill">Friendly set</span>
              <span className="cast-pill">Quick responses</span>
            </div>
          </div>

          <div className="cast-heroCard">
            <div className="cast-heroCardTitle">What to include</div>
            <div className="cast-heroCardSub">
              The more detail you share, the faster we can respond.
            </div>

            <div className="cast-chipRow">
              <span className="cast-chip">Role you want</span>
              <span className="cast-chip">Availability</span>
              <span className="cast-chip">Experience</span>
              <span className="cast-chip">Links (optional)</span>
              <span className="cast-chip">Location</span>
            </div>

            <button className="psoc-btn psoc-btnSecondary cast-heroCardBtn" type="button" onClick={() => scrollToApply(selectedRole)}>
              Jump to application
            </button>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Positions</h2>
            <p className="psoc-sub">
              Review the roles below and apply using the form. Your selected role will be prefilled.
            </p>
          </div>

          <div className="cast-grid">
            {roles.map((r) => (
              <RoleCard
                key={r.title}
                title={r.title}
                tag={r.tag}
                desc={r.desc}
                selected={selectedRole === r.title}
                onSelect={() => {
                  setSelectedRole(r.title);
                  scrollToApply(r.title);
                }}
              />
            ))}
          </div>

          <div className="cast-note">
            Not seeing your exact skill set? Apply anyway and describe what you do. We’re always open to great collaborators.
          </div>
        </div>
      </section>

      {/* Apply */}
      <section className="psoc-section psoc-sectionAlt" ref={applyRef} id="apply">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Apply</h2>
            <p className="psoc-sub">
              Tell us which role you want and why you’d be a great fit. We’ll reach out ASAP.
            </p>
          </div>

          <div className="psoc-contactCard">
            <form onSubmit={handleSubmit} className="psoc-form">
              <div className="psoc-formGrid">
                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" placeholder="Name" required autoComplete="name" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" placeholder="Email" required autoComplete="email" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" type="tel" placeholder="Phone (optional)" autoComplete="tel" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="role">Role</label>
                  <select
                    id="role"
                    name="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="cast-select"
                  >
                    {roles.map((r) => (
                      <option key={r.title} value={r.title}>
                        {r.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="psoc-field psoc-fieldFull">
                  <label className="psoc-label" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Availability, experience, and any links (reel, socials, portfolio)…"
                    required
                    rows={8}
                  />
                </div>
              </div>

              <div className="psoc-formFooter">
                <button className="psoc-btn psoc-btnPrimary" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Submit Application"}
                </button>

                <div className="psoc-status" aria-live="polite">
                  {status.state !== "idle" && (
                    <span className={`psoc-statusMsg psoc-${status.state}`}>{status.message}</span>
                  )}
                </div>
              </div>
            </form>

            <p className="psoc-emailNote">
              Prefer email? Send your role + details to{" "}
              <a className="psoc-link" href="mailto:productionstudioofcolorado@gmail.com">
                productionstudioofcolorado@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="psoc-footer">
        <div className="psoc-container psoc-footerInner">
          <span>© {new Date().getFullYear()} Production Studio of Colorado LLC</span>
          <button className="psoc-btn psoc-btnGhost" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top
          </button>
        </div>
      </footer>
    </div>
  );
}
